import { useDraggable } from '@dnd-kit/core';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SourceDto, ViewSettings, ViewSource } from '@planner/shared';
import { COURSES_LOOKAHEAD_DAYS, COURSES_LOOKBACK_DAYS, isCourseVisible } from '@planner/shared';
import { api } from '../api/client.ts';
import { colorForCourse } from '../lib/color.ts';
import { CINECA_QUERY_RETRY, cinecaRetryDelay } from '../lib/retry.ts';
import { IconGripVertical, IconPencil, IconTrash } from './icons.tsx';

// Server keeps course lists fresh ~7 days and refreshes them in the background; hold them for an hour client side
export const COURSES_STALE_MS = 60 * 60_000;

// Single timestamp: the range is exactly the server max (400 days), so two
// Date.now() calls a millisecond apart would get a permanent 400 until reload
const DAY_MS = 24 * 60 * 60 * 1000;
const COURSES_NOW = Date.now();
export const COURSES_FROM = new Date(COURSES_NOW - COURSES_LOOKBACK_DAYS * DAY_MS).toISOString();
export const COURSES_TO = new Date(COURSES_NOW + COURSES_LOOKAHEAD_DAYS * DAY_MS).toISOString();

// Accent-insensitive, case-insensitive text for matching
export function normalizeSearch(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// Force the server to rebuild a source's course list, then update the shared query cache
export async function refreshCourses(queryClient: QueryClient, sourceId: number): Promise<void> {
  const data = await api.sourceCourses(sourceId, COURSES_FROM, COURSES_TO, true);
  queryClient.setQueryData(['courses', sourceId], data);
}

export function toggleCourse(viewSource: ViewSource, key: string): ViewSource {
  const visible = isCourseVisible(viewSource, key);
  const has = viewSource.courses.includes(key);
  if (viewSource.courseMode === 'exclude') {
    // visible now (not excluded) -> hide by excluding; hidden -> un-exclude
    return { ...viewSource, courses: visible ? [...viewSource.courses, key] : viewSource.courses.filter((c) => c !== key) };
  }
  // include mode: visible means listed; hide by removing, show by adding
  return { ...viewSource, courses: has && visible ? viewSource.courses.filter((c) => c !== key) : [...viewSource.courses, key] };
}

export function SourceBlock({
  source,
  settings,
  onChange,
  onRemove,
  onRename,
}: {
  source: SourceDto;
  settings: ViewSettings;
  onChange: (settings: ViewSettings) => void;
  onRemove: () => void;
  onRename: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const viewSource = settings.sources.find((s) => s.sourceId === source.id);
  const enabled = Boolean(viewSource);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source-${source.id}`,
    data: { kind: 'source', source },
  });

  const coursesQuery = useQuery({
    queryKey: ['courses', source.id],
    queryFn: () => api.sourceCourses(source.id, COURSES_FROM, COURSES_TO),
    enabled: expanded,
    staleTime: COURSES_STALE_MS,
    retry: CINECA_QUERY_RETRY,
    retryDelay: cinecaRetryDelay,
  });

  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  async function refresh() {
    setRefreshing(true);
    setRefreshFailed(false);
    try {
      await refreshCourses(queryClient, source.id);
    } catch {
      setRefreshFailed(true);
    } finally {
      setRefreshing(false);
    }
  }

  function setEnabled(next: boolean) {
    if (next) {
      onChange({ ...settings, sources: [...settings.sources, { sourceId: source.id, courseMode: 'exclude', courses: [] }] });
      setExpanded(true);
    } else {
      onChange({ ...settings, sources: settings.sources.filter((s) => s.sourceId !== source.id) });
    }
  }

  function updateViewSource(next: ViewSource) {
    onChange({ ...settings, sources: settings.sources.map((s) => (s.sourceId === source.id ? next : s)) });
  }

  // Picking a course from a disabled source enables it showing only that course
  function enableWithCourse(key: string) {
    onChange({ ...settings, sources: [...settings.sources, { sourceId: source.id, courseMode: 'include', courses: [key] }] });
  }

  function selectAll() {
    if (viewSource) updateViewSource({ ...viewSource, courseMode: 'exclude', courses: [] });
  }
  function selectNone() {
    if (viewSource) updateViewSource({ ...viewSource, courseMode: 'include', courses: [] });
  }

  const title = source.displayName || (i18n.language === 'en' ? source.titleEn || source.title : source.title);
  const allCourses = coursesQuery.data?.courses ?? [];
  const query = normalizeSearch(search.trim());
  const courses = query
    ? allCourses.filter((c) => normalizeSearch(`${c.name} ${c.nameEn ?? ''} ${c.partition ?? ''}`).includes(query))
    : allCourses;
  const groupLabel = source.groupPath.length > 0 ? source.groupPath.join(' - ') : null;

  return (
    <div className="source-block" style={{ opacity: isDragging ? 0.4 : 1 }}>
      <div className="source-header">
        <button className="drag-handle" ref={setNodeRef} {...attributes} {...listeners} aria-label={t('sidebar.dragHandle')}>
          <IconGripVertical />
        </button>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          aria-label={title}
          style={{ width: 20, height: 20 }}
        />
        <span className="name" title={groupLabel ? `${title} - ${groupLabel}` : title} onClick={() => setExpanded((v) => !v)}>
          {title}
          {groupLabel && <span className="name-group"> - {groupLabel}</span>}
        </span>
        <button className="icon-btn" title={t('sidebar.renameSource')} onClick={onRename}>
          <IconPencil />
        </button>
        <button className="icon-btn" title={t('sidebar.removeSource')} onClick={onRemove}>
          <IconTrash />
        </button>
        <button className="icon-btn" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? t('nav.closeMenu') : t('nav.openMenu')}>
          {expanded ? '▴' : '▾'}
        </button>
      </div>

      {expanded && (
        <div className="course-list">
          {!enabled && courses.length > 0 && <p className="hint">{t('sidebar.previewHint')}</p>}
          {coursesQuery.isLoading && (
            <p className="hint">
              <span className="spinner" />{' '}
              {coursesQuery.isFetching && coursesQuery.failureCount > 0 ? t('calendar.retrying') : t('sidebar.loadingCourses')}
            </p>
          )}
          {coursesQuery.isError && (
            <p className="error-text">
              {t('sidebar.sourceError')}{' '}
              <button className="text-btn" onClick={() => coursesQuery.refetch()}>
                {t('action.retry')}
              </button>
            </p>
          )}
          {allCourses.length > 0 && (
            <input
              type="search"
              className="course-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('sidebar.searchCourses')}
              aria-label={t('sidebar.searchCourses')}
            />
          )}
          {!coursesQuery.isLoading && courses.length === 0 && <p className="hint">{t('sidebar.noCourses')}</p>}
          {allCourses.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {viewSource && (
                <>
                  <button className="text-btn" onClick={selectAll}>
                    {t('sidebar.selectAll')}
                  </button>
                  <button className="text-btn" onClick={selectNone}>
                    {t('sidebar.selectNone')}
                  </button>
                </>
              )}
              <button className="text-btn" style={{ marginLeft: 'auto' }} title={t('sidebar.refreshHint')} onClick={() => void refresh()} disabled={refreshing}>
                {refreshing && <span className="spinner" />} {t('sidebar.refreshCourses')}
              </button>
            </div>
          )}
          {refreshFailed && <p className="error-text">{t('sidebar.refreshError')}</p>}
          {courses.map((c) => {
            const name = i18n.language === 'en' && c.nameEn ? c.nameEn : c.name;
            const visible = viewSource ? isCourseVisible(viewSource, c.key) : false;
            return (
              <label className="course-row" key={c.key}>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => (viewSource ? updateViewSource(toggleCourse(viewSource, c.key)) : enableWithCourse(c.key))}
                />
                <span className="course-dot" style={{ background: colorForCourse(c.key, settings.colors) }} />
                <span className="course-name" title={name}>
                  {name}
                  {c.partition ? ` - ${c.partition}` : ''}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
